import {
  LBButton,
  LBInput,
  LBSelect,
  LBSelectContent,
  LBSelectItem,
  LBSelectTrigger,
  LBSelectValue,
  LBLoader,
  LBXIcon
} from '@linearb/buzz'
import CustomTable from '../views/OrganizationDetails/CustomTable'
import DashboardLayout from '../layouts/DashboardLayout'

interface ToggleStatesList {
  [key: string]: string
}

interface SortConfig {
  key: string
  direction: 'asc' | 'desc'
}

interface FilteredTableProps {
  title?: string
  toggleStatesList?: ToggleStatesList
  /** Count to show next to each toggle, keyed by the toggle's value */
  toggleCounts?: Record<string, number> | null
  tableRows: unknown[]
  tableColumns: unknown[]
  tableId: string
  additionalButtonText?: string
  showAdditionalButton?: boolean
  onAdditionalButtonClick?: () => void
  additionalButtonIcon?: React.ReactNode
  additionalButtonDisabled?: boolean
  onSort?: ((key: string) => void) | null
  sortConfig?: SortConfig | null
  // Query-based props
  isLoading: boolean
  isFetching?: boolean
  totalCount: number
  page?: number
  rowsPerPage?: number
  toggleMenu?: string
  inputValue?: string
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onToggleChange?: (value: string) => void
  onPageChange?: (page: number) => void
  onRowsPerPageChange?: (value: string) => void
  // Second toggle group (optional)
  secondToggleStatesList?: ToggleStatesList | null
  secondToggleMenu?: string | null
  onSecondToggleChange?: ((value: string) => void) | null
  // Custom filter component (optional)
  customFilter?: React.ReactNode | null
  // Custom action component (replaces additionalButton when provided)
  customAction?: React.ReactNode | null
  // Rendered inline at the end of the toggle row (next to PAID/TRIAL/FREE etc.)
  afterToggles?: React.ReactNode | null
  // Rendered inline to the right of the search input
  afterSearch?: React.ReactNode | null
  // Custom search labels (optional)
  searchLabel?: string
  searchPlaceholder?: string
  // Layout options
  hideSearch?: boolean
  hideResultsCount?: boolean
  hidePagination?: boolean
  noLayout?: boolean
  // Empty state message (shown when no results and not loading)
  emptyMessage?: string
}

const FilteredTable: React.FC<FilteredTableProps> = ({
  title,
  toggleStatesList = {},
  toggleCounts = null,
  tableRows,
  tableColumns,
  tableId,
  additionalButtonText = '',
  showAdditionalButton = false,
  onAdditionalButtonClick = () => {},
  additionalButtonIcon = null,
  additionalButtonDisabled = false,
  onSort = null,
  sortConfig = null,
  // Query-based props
  isLoading,
  isFetching = false,
  totalCount,
  page = 0,
  rowsPerPage = 10,
  toggleMenu = '',
  inputValue = '',
  onSearchChange = () => {},
  onToggleChange = () => {},
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  // Second toggle group (optional)
  secondToggleStatesList = null,
  secondToggleMenu = null,
  onSecondToggleChange = null,
  // Custom filter component (optional)
  customFilter = null,
  // Custom action component (replaces additionalButton when provided)
  customAction = null,
  afterToggles = null,
  afterSearch = null,
  // Custom search labels (optional)
  searchLabel = '',
  searchPlaceholder = 'Organization name or ID',
  // Layout options
  hideSearch = false,
  hideResultsCount = false,
  hidePagination = false,
  noLayout = false,
  emptyMessage = 'No results found.'
}) => {
  const totalPages = Math.ceil(totalCount / rowsPerPage)
  const showLoading = isLoading || (isFetching && !tableRows?.length)
  const showOverlay = isFetching && !isLoading && tableRows?.length > 0

  const handleClearSearch = (): void => {
    onSearchChange({
      target: { value: '' }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const Wrapper = noLayout ? 'div' : DashboardLayout

  return (
    <Wrapper>
      {title && <h1 className='text-2xl font-semibold mb-4'>{title}</h1>}
      {/* Header with search and button */}
      {(!hideSearch || customFilter) && (
        <div className='flex flex-col md:flex-row md:flex-wrap items-center md:items-start gap-4 mb-6'>
          {!hideSearch && (
            <div className='w-full md:w-auto'>
              {searchLabel && (
                <label className='block text-sm font-medium text-foreground mb-2'>
                  {searchLabel}
                </label>
              )}
              <div className='flex items-center gap-3 w-full'>
                <div className='relative flex-1 min-w-0'>
                  <LBInput
                    placeholder={searchPlaceholder}
                    value={inputValue}
                    onChange={onSearchChange}
                    autoComplete='off'
                    className='w-full md:w-96 pr-10'
                  />
                  {inputValue && (
                    <LBButton
                      onClick={handleClearSearch}
                      variant='ghost'
                      size='sm'
                      className='absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-accent'
                    >
                      <LBXIcon className='h-4 w-4 text-muted-foreground' />
                    </LBButton>
                  )}
                </div>
                {afterSearch}
              </div>
            </div>
          )}
          {(customFilter || customAction || showAdditionalButton) && (
            <div className='w-full md:w-auto md:ml-auto flex items-start gap-3'>
              {customFilter}
              {customAction ? (
                <div className='hidden md:block shrink-0'>{customAction}</div>
              ) : (
                showAdditionalButton && (
                  <LBButton
                    onClick={onAdditionalButtonClick}
                    className='hidden md:inline-flex shrink-0'
                    disabled={additionalButtonDisabled}
                  >
                    {additionalButtonIcon && (
                      <span className='mr-1.5 inline-flex'>
                        {additionalButtonIcon}
                      </span>
                    )}
                    {additionalButtonText}
                  </LBButton>
                )
              )}
            </div>
          )}
        </div>
      )}

      {/* Toggle tabs */}
      {(Object.keys(toggleStatesList).length > 0 ||
        secondToggleStatesList ||
        afterToggles) && (
        <div className='flex flex-wrap items-center gap-y-3 mb-6'>
          <div className='flex-1' />
          {/* min-w-0 so the pill groups' max-w-full resolves against this row
              rather than their own max-content width */}
          <div className='flex flex-wrap items-center gap-3 md:gap-6 min-w-0'>
            {Object.keys(toggleStatesList).length > 0 && (
              <div className='inline-flex max-w-full overflow-x-auto rounded-full border border-border p-1 bg-muted'>
                {Object.entries(toggleStatesList).map(([key, value]) => (
                  <LBButton
                    key={value}
                    variant={toggleMenu === value ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => onToggleChange(value)}
                    className={`rounded-full ${
                      toggleMenu === value
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    {key}
                    {toggleCounts?.[value] != null && (
                      <span className='ml-1.5 opacity-60'>
                        {toggleCounts[value].toLocaleString()}
                      </span>
                    )}
                  </LBButton>
                ))}
              </div>
            )}
            {secondToggleStatesList && (
              <div className='inline-flex max-w-full overflow-x-auto rounded-full border border-border p-1 bg-muted'>
                {Object.entries(secondToggleStatesList).map(([key, value]) => (
                  <LBButton
                    key={key}
                    variant={secondToggleMenu === value ? 'default' : 'ghost'}
                    size='sm'
                    onClick={() => onSecondToggleChange?.(value)}
                    className={`rounded-full ${
                      secondToggleMenu === value
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    {key}
                  </LBButton>
                ))}
              </div>
            )}
          </div>
          <div className='flex-1 flex justify-end'>{afterToggles}</div>
        </div>
      )}

      {/* Results count */}
      {!hideResultsCount && (
        <div className='flex items-center gap-2 mb-4'>
          <p className='text-base font-semibold text-foreground'>
            Results ({totalCount})
          </p>
          {isFetching && !isLoading && <LBLoader className='h-4 w-4' />}
        </div>
      )}

      {/* Table or Loading */}
      {showLoading ? (
        <div className='flex justify-center items-center py-20'>
          <LBLoader className='h-12 w-12' />
        </div>
      ) : !tableRows?.length && !isFetching ? (
        <div className='text-sm text-muted-foreground py-4 text-center'>
          {emptyMessage}
        </div>
      ) : (
        <div className='relative overflow-x-auto'>
          {showOverlay && (
            <div className='absolute inset-0 bg-background/60 z-10 flex justify-center items-center'>
              <LBLoader className='h-8 w-8' />
            </div>
          )}
          <CustomTable
            tableRows={tableRows}
            tableWidth='max(100%, 700px)'
            columns={tableColumns}
            key={tableId}
            onSort={onSort}
            sortConfig={sortConfig}
          />
        </div>
      )}

      {/* Pagination */}
      {!hidePagination && totalPages > 0 && (
        <div className='flex flex-col md:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-3'>
          <div className='flex flex-col sm:flex-row items-center gap-2 sm:gap-4'>
            <span className='text-sm text-muted-foreground'>
              Showing {page * rowsPerPage + 1} to{' '}
              {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount}{' '}
              results
            </span>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-muted-foreground'>
                Rows per page:
              </span>
              <LBSelect
                value={String(rowsPerPage)}
                onValueChange={onRowsPerPageChange}
              >
                <LBSelectTrigger className='w-20 h-8'>
                  <LBSelectValue />
                </LBSelectTrigger>
                <LBSelectContent>
                  <LBSelectItem value='10'>10</LBSelectItem>
                  <LBSelectItem value='25'>25</LBSelectItem>
                  <LBSelectItem value='50'>50</LBSelectItem>
                  <LBSelectItem value='100'>100</LBSelectItem>
                </LBSelectContent>
              </LBSelect>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <LBButton
              variant='outline'
              size='sm'
              onClick={() => onPageChange(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </LBButton>
            <span className='px-4 py-2 text-sm text-foreground'>
              Page {page + 1} of {totalPages || 1}
            </span>
            <LBButton
              variant='outline'
              size='sm'
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </LBButton>
          </div>
        </div>
      )}
    </Wrapper>
  )
}

export default FilteredTable
